import type { CSSProperties, ComponentProps, Ref } from 'react'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'

import { cn } from '@/lib/utils'

// Implementation notes:
// - The Radix viewport owns scrolling; do not put overflow-y-auto on the root and expect
//   the styled scrollbar to appear.
// - In header/body/footer modals or other flex columns, every parent between the fixed
//   height container and ScrollArea needs min-h-0, and ScrollArea should usually be
//   className="min-h-0 flex-1". DialogContent is grid by default, so modal layouts with
//   a scrollable middle section should opt into flex with className="!flex ... flex-col".
// - If the root uses max-height instead of a fixed height, pass a matching
//   viewportClassName (for example "max-h-48") so the viewport is the constrained element.
export type ScrollAreaScrollbars = 'vertical' | 'horizontal' | 'both' | 'none'

export type ScrollAreaProps = ComponentProps<typeof ScrollAreaPrimitive.Root> & {
  viewportClassName?: string
  viewportRef?: Ref<HTMLDivElement>
  viewportStyle?: CSSProperties
  scrollbars?: ScrollAreaScrollbars
}

function ScrollArea({
  className,
  viewportClassName,
  viewportRef,
  viewportStyle,
  children,
  scrollbars = 'vertical',
  ...props
}: ScrollAreaProps) {
  const showHorizontalScrollbar = scrollbars === 'horizontal' || scrollbars === 'both'
  const showVerticalScrollbar = scrollbars === 'vertical' || scrollbars === 'both'

  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      className={cn('relative', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        data-slot="scroll-area-viewport"
        className={cn(
          'focus-visible:ring-ring/50 size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1',
          viewportClassName
        )}
        style={viewportStyle}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {showVerticalScrollbar ? <ScrollBar /> : null}
      {showHorizontalScrollbar ? <ScrollBar orientation="horizontal" /> : null}
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
}

export type ScrollBarProps = ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>

function ScrollBar({ className, orientation = 'vertical', ...props }: ScrollBarProps) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        'flex touch-none p-px transition-colors select-none',
        orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent',
        orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent',
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="bg-border relative flex-1 rounded-full"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
}

export { ScrollArea, ScrollBar }
